import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { StorageService } from '@coin-depo-core/storage';
import { CoinFacade, COIN_FACADE } from '@coin-depo-core/coin';

import { VerificationFacade } from '@coin-depo-core/verification';

import type  { UserDetailsStatus } from '@coin-depo-types';

@Injectable({
  providedIn: 'root',
})
export class VerificationService implements VerificationFacade {
  private identityVerification$ = new BehaviorSubject<boolean>(
    this.storage.get('identityVerification', false)
  );
  cachedIdentityVerification$ = this.identityVerification$.asObservable();

  private sumsubKYC$ = new BehaviorSubject<boolean>(
    this.storage.get('sumsubKYC', false)
  );
  cachedSumsubKYC$ = this.sumsubKYC$.asObservable();

  private isSumsubKYCRequired$ = new BehaviorSubject<boolean>(
    this.storage.get('isSumsubKYCRequired', false)
  );
  cachedIsSumsubKYCRequired$ = this.isSumsubKYCRequired$.asObservable();

  private sumsubPOA$ = new BehaviorSubject<boolean>(
    this.storage.get('sumsubPOA', false)
  );
  cachedSumsubPOA$ = this.sumsubPOA$.asObservable();

  private isSumsubPOARequired$ = new BehaviorSubject<boolean>(
    this.storage.get('isSumsubPOARequired', false)
  );
  cachedIsSumsubPOARequired$ = this.isSumsubPOARequired$.asObservable();

  private TOTPEnabledVerification$ = new BehaviorSubject<boolean>(
    this.storage.get('TOTPEnabledVerification', false)
  );
  cachedTOTPEnabledVerification$ = this.TOTPEnabledVerification$.asObservable();

  private userDetailsStatus$ = new BehaviorSubject<UserDetailsStatus | null>(
    this.storage.get('userDetailsStatus', null)
  );
  cachedTUserDetailsStatus$ = this.userDetailsStatus$.asObservable();

  cachedFullVerification$: Observable<boolean> = combineLatest([
    this.cachedIdentityVerification$,
    this.cachedTOTPEnabledVerification$,
  ]).pipe(
    map(([identityVerification, isTOTPEnabled]) =>
      identityVerification && isTOTPEnabled
    )
  );

  constructor(
    private storage: StorageService,
    @Inject(COIN_FACADE) private coinsService: CoinFacade,
  ) {}

  identityVerificationSnapshot() {
    return this.identityVerification$.value;
  }

  sumsubKYCSnapshot() {
    return this.sumsubKYC$.value;
  }

  userDetailsStatusSnapshot() {
    return this.userDetailsStatus$.value;
  }

  TOTPEnabledVerificationSnapshot() {
    return this.TOTPEnabledVerification$.value;
  }

  setTOTPEnabledVerification(enable: boolean) {
    this.TOTPEnabledVerification$.next(enable);
    this.storage.set('TOTPEnabledVerification', enable);
  }

  setIdentityVerification(enable: boolean) {
    this.identityVerification$.next(enable);
    this.storage.set('identityVerification', enable);
  }

  setSumsubKYC(enable: boolean) {
    this.sumsubKYC$.next(enable);
    this.storage.set('sumsubKYC', enable);
  }

  setIsSumsubKYCRequired(enable: boolean) {
    this.isSumsubKYCRequired$.next(enable);
    this.storage.set('isSumsubKYCRequired', enable);
  }

  setSumsubPOA(enable: boolean) {
    this.sumsubKYC$.next(enable);
    this.storage.set('sumsubPOA', enable);
  }

  setIsSumsubPOARequired(enable: boolean) {
    this.isSumsubKYCRequired$.next(enable);
    this.storage.set('isSumsubPOARequired', enable);
  }

  setUserDetailsStatus(status: UserDetailsStatus | null) {
    if (
      this.userDetailsStatus$.value !== status &&
      status === 'verified' &&
      this.userDetailsStatus$.value !== null
    ) {
      this.coinsService.updateSupportedCoins().subscribe();
    }
    this.userDetailsStatus$.next(status);
    this.storage.set('userDetailsStatus', status);
  }
}
